import { filter } from 'lodash-es';
import moment from 'moment';

/**
 * Method to checks for overlaps on dates and returns an array of the dates that are present in
 * both the new allocation dates array and the old allocations dates array.
 *
 * @param newDates
 * @param oldDates
 */
export const checkForOverlap = (newDates, oldDates) => {
    let overlapDates = [];
    // Distinct the old dates to ensure that dates are not appearing twice
    newDates.forEach((date) => {
        if (oldDates.indexOf(date) !== -1) {
            overlapDates.push(date);
        }
    });
    return generatePeriod(overlapDates);
};

/**
 * Returns an array of periods based on the overlaps dates.
 *
 * @param overlapDates
 */
export const generatePeriod = (overlapDates) => {
    let periods     = [];
    let from        = null;
    let to          = null;
    let current     = null;

    if(overlapDates.length > 0) {
        overlapDates.forEach((date) => {
            if (from === null) {
                current = moment(date).startOf('day');
                from = date;
            } else {
                let currentTomorrowStr = current.add(1, 'days').format('Y-MM-DD');
                if (currentTomorrowStr !== date) {
                    to = current.format('DD/MM/Y');
                    periods.push({from: moment(from, 'YYYY-MM-DD').format('DD/MM/Y'), to: to});
                    current = moment(date).startOf('day');
                    from = date;
                }
            }
        });
        // Add the last period
        to = overlapDates[overlapDates.length -1];
        to = moment(to, 'YYYY-MM-DD');
        to.add(1, 'days');
        periods.push({from: moment(from, 'YYYY-MM-DD').format('DD/MM/Y'), to: to.format('DD/MM/Y')});
    }
    return periods;
};

/**
 * Returns a calendar of dates based on a from and a to date. All dates a string formatted YYYY-MM-DD
 *
 * @param from
 * @param to
 * @returns {Array}
 */
export const generateValidatorCalendar = (from, to) => {
    let dates = [];
    // Convert dates to moment dates
    from = convertStringDateToDate(from);
    to = convertStringDateToDate(to);
    // Create dates in period from - to
    do {
        dates.push(from.format('Y-MM-DD'));
    }
    while(from.add(1, 'days').diff(to) < 0);
    return dates;
};

/**
 * Returns a calendar of dates based on a from and a to date with one added day. All dates a string formatted YYYY-MM-DD
 *
 * @param from
 * @param to
 * @returns {Array}
 */
export const generateValidatorCalendarAddOneDay = (from, to) => {
    let dates = [];
    // Convert dates to moment dates
    from = convertStringDateToDate(from);
    to = convertStringDateToDate(to);
    // Create dates in period from - to
    do {
        dates.push(from.format('Y-MM-DD'));
    }
    while(from.add(1, 'days').diff(to) <= 0);
    return dates;
};

/**
 * Returns a calendar of dates based on a from and a to date. All dates a string formatted YYYY-MM-DD
 *
 * @param from
 * @param to
 * @returns {Array}
 */
export const generateCalendar = (from, to) => {
    let dates = [];
    // Convert dates to moment dates
    from = convertStringDateToDate(from);
    to = convertStringDateToDate(to);
    // Create dates in period from - to
    do {
        dates.push(from.format('Y-MM-DD'));
    }
    while(from.add(1, 'days').diff(to) <= 0);
    return dates;
};

/**
 * List allocations within period based on from and to date.
 *
 * @param from
 * @param to
 * @param allocations
 * @param changeRequests
 * @returns {Array}
 */
export const listOfAllocationsWithinPeriod = (from, to, allocations, changeRequests) => {
    let allocationsWithinPeriod = [];
    // Subtract 1 day from the to date
    const periodToExcludeLastDay = convertStringDateToDate(to).subtract(1, 'days');
    let newDates = (from && to) ? generateCalendar(from, periodToExcludeLastDay) : [];
    filter(allocations, (accommodation) => {
        filter(accommodation, (allocation) => {
            // Subtract 1 day from the to date
            const allocationToExcludeLastDay = convertStringDateToDate(allocation.to).subtract(1, 'days');
            let oldDates = generateCalendar(allocation.from, allocationToExcludeLastDay);
            if(checkForOverlap(newDates, oldDates).length > 0) {
                //Calculate from to dates to check for requests
                const period = calculateNewPeriodFromTo(from, to, allocation);
                // Add release status for allocation in period
                allocationsWithinPeriod.push({
                    allocation: allocation,
                    isReleased: isReleased(to, allocation) || isSoldOut(from, to, allocation),
                    hasRequest: hasRequest(period.from, period.to, changeRequests)
                })
            }
        })
    });
    return allocationsWithinPeriod;
};

/**
 * Check if the allocation is released in the period
 *
 * @param to
 * @param allocation
 */
export const isReleased = (to, allocation) => {
    const today = moment().startOf('day');
    // Convert dates to moment dates
    to = convertStringDateToDate(to);
    const releaseDate = (allocation.releaseDate !== null) ? convertStringDateToDate(allocation.releaseDate) : calculateLatestReleaseDate(allocation, to);
    return today.diff(releaseDate) >= 0;
};

/**
 * Check if the allocation has any changes requested.
 *
 * @param from
 * @param to
 * @param changeRequests
 */
export const hasRequest = (from, to, changeRequests) => {
    const fromToCalendar = generateValidatorCalendar(from, to);
    return filter(changeRequests, (request) => {
        const requestCalendar = generateValidatorCalendar(request.from, request.to);
        return checkForOverlap(requestCalendar, fromToCalendar).length > 0;
    }).length > 0;
};

/**
 * Calculate the to date to use for the modals new allocations, which is used
 * when adding new allocation to released stock.
 *
 * @param calendarTo
 * @param allocationTo
 * @returns {*}
 */
export const calculateToDate = (calendarTo, allocationTo) => {
    let calendarToDate = convertStringDateToDate(calendarTo);
    let allocationToDate = convertStringDateToDate(allocationTo);
    return (allocationToDate.diff(calendarToDate) < 0) ? allocationToDate.format('DD/MM/YYYY') : calendarTo;
};

/**
 * Convert a string date to a moment date.
 *
 * @param date
 * @returns {*}
 */
export const convertStringDateToDate = (date) => {
    if (typeof(date) === "string") {
        if(date.indexOf('-') !== -1) {
            return moment(date, 'YYYY-MM-DD').startOf('day');
        } else if (date.indexOf('/') !== -1) {
            return moment(date, 'DD/MM/YYYY').startOf('day');
        } else {
            return null;
        }
    }
    return moment(date);
};

/**
 * Converts a date to string.
 *
 * @param date
 */
export const convertDateToString = (date) => {
    return convertStringDateToDate(date).format('YYYY-MM-DD');
};

/**
 * Calculate the latest release date in the period chosen for a specific allocation.
 *
 * @param allocation
 * @param to
 */
const calculateLatestReleaseDate = (allocation, to) => {
    const allocationEndDate = moment(allocation.to).startOf('day');
    let finalDate = (to.diff(allocationEndDate) < 0) ? to : allocationEndDate;
    return finalDate.subtract(allocation.releaseDays + 1, 'days');
};

/**
 * Calculates which from and to date to use for the allocation, if the from date is before the
 * calendar from date, the calendar from date will be used, and same goes with the to date to
 * ensure that the allocation is only edited within the chosen period.
 *
 * @param calendarFrom
 * @param calendarTo
 * @param allocation
 * @returns {{from: string, to: string}}
 */
export const calculatePeriodFromTo = (calendarFrom, calendarTo, allocation) => {
    const calendarFromMoment = convertStringDateToDate(calendarFrom);
    const calendarToMoment = convertStringDateToDate(calendarTo);
    const allocationFromMoment = convertStringDateToDate(allocation.from);
    const allocationToMoment = convertStringDateToDate(allocation.to);

    let from = calendarFromMoment.diff(allocationFromMoment) > 0 ? calendarFromMoment : allocationFromMoment;
    let to = calendarToMoment.diff(allocationToMoment) < 0 ? calendarToMoment : allocationToMoment;

    return {from: from, to: to};
};

/**
 * Calculates the from and to date when trying to add new allocations to a released period.
 *
 * @param calendarFrom
 * @param calendarTo
 * @param allocation
 * @returns {{from: *|moment.Moment, to: *|moment.Moment}}
 */
export const calculateNewPeriodFromTo = (calendarFrom, calendarTo, allocation) => {
    const calendarFromMoment = convertStringDateToDate(calendarFrom);
    const calendarToMoment = convertStringDateToDate(calendarTo);
    const allocationFromMoment = convertStringDateToDate(allocation.from);
    const allocationToMoment = convertStringDateToDate(allocation.to);

    let from = calendarFromMoment.diff(allocationFromMoment) > 0 ? calendarFromMoment : allocationFromMoment;
    let to = calendarToMoment.diff(allocationToMoment) < 0 ? calendarToMoment : allocationToMoment;

    return {from: from.format('DD/MM/YYYY'), to: to.format('DD/MM/YYYY')};
};

/**
 * Takes a date and adds a day to it and returns as string in format DD/MM7YYYY
 *
 * @param dateString
 */
export const addDay = (dateString) => {
    const date = convertStringDateToDate(dateString);
    return date.add(1, 'days').format('DD/MM/YYYY');
};

/**
 * Check the selected allocation for pending requests.
 *
 * @param allocation
 * @param changeRequests
 * @returns {Array}
 */
export const changeRequestForAllocation = (allocation, changeRequests) => {
    // Create cloned dates to ensure that the state is not altered
    const allocationFrom = convertStringDateToDate(allocation.from);
    const allocationTo = convertStringDateToDate(allocation.to);
    const allocationCalendar = generateValidatorCalendar(allocationFrom, allocationTo);
    return filter(changeRequests, (request) => {
        const requestCalendar = generateValidatorCalendar(request.from, request.to);
        return checkForOverlap(requestCalendar, allocationCalendar).length > 0;
    });
};

/**
 * Check that the bookings is for the full chosen period.
 *
 * @param allocationPeriod
 * @param bookingPeriod
 * @returns {boolean}
 */
export const bookingForFullPeriod = (allocationPeriod, bookingPeriod) => {
    return filter(allocationPeriod, (date) => {
        return bookingPeriod.indexOf(date) === -1;
    }).length === 0;
};

/**
 * Check the bookings in the chosen period and calculate if the number of bookings equals the number of units.
 *
 * @param calendarFrom
 * @param calendarTo
 * @param allocation
 * @returns {boolean}
 */
export const isSoldOut = (calendarFrom, calendarTo, allocation) => {
    const period = calculateNewPeriodFromTo(calendarFrom, calendarTo, allocation);
    const allocationPeriod = generateValidatorCalendar(period.from, period.to);
    let bookingsInPeriod = filter(allocation.bookings, (booking) => {
        const bookingPeriod = generateValidatorCalendarAddOneDay(booking.from, booking.to);
        return bookingForFullPeriod(allocationPeriod, bookingPeriod);
    });
    return allocation.units === bookingsInPeriod.length;
};

/**
 * Get the indices of the request chosen to highlight in the calendar.
 *
 * @param request
 * @returns {Array}
 */
export const getCalendarHighlightIndices = (request) => {
    let indices = [];
    let fromIndex = parseInt(convertStringDateToDate(request.from).format('DDD')) - 1;
    const toIndex = parseInt(convertStringDateToDate(request.to).format('DDD')) - 1;
    for(fromIndex; fromIndex < toIndex; fromIndex++) {
        indices.push(fromIndex);
    }
    return indices;
};

/**
 * Calculate the calendar dates, if 1st of november then year will be next year.
 *
 * @returns {{}}
 */
export const calculateCalendarDates = () => {
    const today = moment();
    const startOfNovember = moment(today.year() + '-10-01 00:00:000');
    const currentMonthNumber = today.format('M');

    let year = today.year()+1; // If not after 1st november set year to current
    let startMonth = 0;
    let endMonth = 6;

    if(today.diff(startOfNovember) < 0) {
        year = today.year();
        startMonth = currentMonthNumber < 4 ? 0 : currentMonthNumber < 7 ? 3 : 6;
        endMonth = startMonth + 6;
    }
    return {year: year, startMonth: startMonth, endMonth: endMonth};
};

/**
 * Get the season period to and from dates.
 *
 * @param year
 * @returns {{from: string, to: string}}
 */
export const getSeasonPeriod = (year) => {
    return {
        from: year + '-01-01',
        to: (parseInt(year) + 1) + '-01-01'
    };
};



// WEBPACK FOOTER //
// ./src/services/DateToolService.js